// hooks/useClimbingData.js
import { useMemo, useState } from 'react';
import { CLIMBING_STYLES } from '@utils/constants.js';
import { calculateKeyMetrics } from '@utils/calculators/keyMetrics.js';

export const useClimbingData = (data, options = {}) => {
  const {
    initialStyle = CLIMBING_STYLES.SPORT,
    initialGradeSystem = 'ewbank',
    initialYear = 'all',
    initialMonth = 'all'
  } = options;

  const [gradeSystem, setGradeSystem] = useState(initialGradeSystem);
  const [selectedYear, setSelectedYear] = useState(initialYear);
  const [selectedMonth, setSelectedMonth] = useState(initialMonth);
  const [selectedCrag, setSelectedCrag] = useState('all');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [climbingStyle, setClimbingStyle] = useState(initialStyle);

  const { years, crags, countries } = useMemo(() => {
    if (!data?.length) return { years: [], crags: [], countries: [] };
    
    const allDates = data.map(ascent => ascent.year);
    const allCountries = data.map(ascent => ascent['Country']);

    const cragCounts = data.reduce((acc, ascent) => {
      const crag = ascent['Crag Name'];
      acc[crag] = (acc[crag] || 0) + 1;
      return acc;
    }, {});

    const significantCrags = Object.entries(cragCounts)
      .filter(([_, count]) => count >= 30)
      .sort((a, b) => b[1] - a[1])
      .map(([crag, count]) => ({
        name: crag,
        count: count
      }));

    return {
      years: [...new Set(allDates)].sort((a, b) => b - a),
      crags: significantCrags,
      countries: [...new Set(allCountries)].sort()
    };
  }, [data]);

  const filteredData = useMemo(() => {
    if (!data?.length) return [];
    
    return data.filter(ascent => {
      const styleMatch = (() => {
        switch (climbingStyle) {
          case CLIMBING_STYLES.SPORT:
            return ascent["Route Gear Style"] === "Sport" || 
                   (!["Ice", "Trad", "Alpine", "Boulder"].includes(ascent["Route Gear Style"]));
          case CLIMBING_STYLES.TRAD:
            return ascent["Route Gear Style"] === "Trad";
          case CLIMBING_STYLES.BOULDER:
            return ascent["Route Gear Style"] === "Boulder";
          default:
            return false;
        }
      })();

      const yearMatch = selectedYear === 'all' ||
        ascent.year === Number(selectedYear);
      const monthMatch = selectedMonth === 'all' ||
        ascent.month === Number(selectedMonth);
      const cragMatch = selectedCrag === 'all' ||
        ascent["Crag Name"] === selectedCrag;
      const countryMatch = selectedCountry === 'all' ||
        ascent["Country"] === selectedCountry;

      return styleMatch && yearMatch && monthMatch && cragMatch && countryMatch;
    });
  }, [selectedYear, selectedMonth, selectedCrag, selectedCountry, climbingStyle, data]);

  const metrics = useMemo(() => 
    filteredData.length ? calculateKeyMetrics(filteredData, climbingStyle, selectedYear) : null,
    [filteredData, climbingStyle, selectedYear]
  );

  return {
    gradeSystem,
    setGradeSystem,
    selectedYear,
    setSelectedYear,
    selectedMonth,
    setSelectedMonth,
    selectedCrag,
    setSelectedCrag,
    selectedCountry,
    setSelectedCountry,
    climbingStyle,
    setClimbingStyle,
    filteredData,
    metrics,
    years,
    crags,
    countries
  };
};